import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Project, ProjectTask } from "../types";

export const useAssignedProjects = () => {
  const [assignedProjects, setAssignedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Load projects where user is assigned as a resource
  const loadAssignedProjects = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user?.email) {
        console.error('User not authenticated or email not available');
        return;
      }
      
      // Get unique project IDs where user is assigned as a resource
      const { data: assignments, error: assignmentError } = await supabase
        .from('resource_assignment')
        .select('project_id')
        .eq('email', user.email);
        
      if (assignmentError) {
        console.error('Error loading resource assignments:', assignmentError);
        return;
      }

      if (!assignments || assignments.length === 0) {
        setAssignedProjects([]);
        return;
      }

      // Get unique project IDs
      const uniqueProjectIds = [...new Set(assignments.map(a => a.project_id))];
      
      // Fetch the actual projects
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .in('id', uniqueProjectIds)
        .order('created_at', { ascending: false });
        
      if (projectsError) {
        console.error('Error loading assigned projects:', projectsError);
        return;
      }
      
      const mappedProjects: Project[] = (projects || []).map(project => {
        // Parse project_tasks from Json to ProjectTask[]
        let parsedTasks: ProjectTask[] = [];
        if (project.project_tasks) {
          try {
            // Handle both array and string JSON formats
            const tasksData = Array.isArray(project.project_tasks) 
              ? project.project_tasks 
              : JSON.parse(JSON.stringify(project.project_tasks));
            
            if (Array.isArray(tasksData)) {
              parsedTasks = tasksData.map((task: any) => ({
                name: task.name || '',
                duration: task.duration || 1,
                progress: task.progress || 0,
                phase: task.phase || 'Main Tasks',
                startDate: task.startDate || '',
                endDate: task.endDate || '',
                dependencies: task.dependencies || '',
                isCritical: task.isCritical || false,
                isMilestone: task.isMilestone || false,
                id: task.id,
                taskId: task.taskId
              }));
            }
          } catch (parseError) {
            console.error('Error parsing project tasks:', parseError);
          }
        }
        
        return {
          id: project.id,
          name: project.project_name,
          description: project.description || '',
          projectLead: project.project_lead || '',
          startDate: project.start_date || '',
          endDate: project.end_date || '',
          template: project.template || '',
          duration: project.duration || 0,
          excludeWeekends: project.exclude_weekends,
          lastUpdated: new Date(project.updated_at).toLocaleDateString(),
          tasks: parsedTasks
        };
      });
      
      setAssignedProjects(mappedProjects);
      
    } catch (error) {
      console.error('Error in loadAssignedProjects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize assigned projects on mount
  useEffect(() => {
    loadAssignedProjects();
  }, []);

  return {
    assignedProjects,
    loading,
    loadAssignedProjects
  };
};